import { omit } from "lodash";
import { createHooks } from "hookable";
import { withQuery } from "ufo";
import { useCheckoutStore } from "../stores/checkout";
import type {
  Checkout,
  CheckoutCreateInput,
  CheckoutUpdateInput,
} from "@orie/sdk";
import { salesApi } from "~/services/sales-api";

export const useCheckout = (token?: string) => {
  const store = useCheckoutStore(token);
  const { loading } = storeToRefs(store);
  const bus = createHooks();
  const sales = salesApi();

  const onError = (cb: { (error: any): void }) => bus.hook("error", cb);

  const load = async (token: string): Promise<Checkout | null> => {
    store.fetching = true;

    const result = await sales.checkout
      .get(token)
      .catch((error) => {
        bus.callHook("error", error);
      })
      .finally(() => {
        store.fetching = false;
      });

    if (result) {
      store.fill(result);
      return store.checkout;
    }

    return null;
  };

  const create = async (data: CheckoutCreateInput = {}): Promise<boolean> => {
    store.creating = true;
    const route = useRoute();

    const result = await sales.checkout
      .create({
        success_url: withQuery(
          `${window.location.origin}/thank-you`,
          route.query
        ),
        cancel_url: window.location.href,
        ...data,
      })
      .catch((error) => {
        bus.callHook("error", error);
      })
      .finally(() => {
        store.creating = false;
      });

    if (result) {
      store.fill(result);
      return true;
    }

    return false;
  };

  const update = async (data: CheckoutUpdateInput): Promise<boolean> => {
    store.updating = true;
    const result = await sales.checkout
      .update(store.checkout.token, data)
      .catch((error) => {
        bus.callHook("error", error);
      })
      .finally(() => {
        store.updating = false;
      });

    if (result) {
      store.fill(result);
      return true;
    }

    return false;
  };

  const wrapFnReturnCheckout = <A extends any[]>(
    fn: (token: string, ...args: A) => Promise<Checkout>
  ) => {
    return (...args: A): Promise<Checkout> => {
      return new Promise((resolve) => {
        store.updating = true;
        fn(store.checkout.token, ...args)
          .then((c) => {
            if (c) {
              store.fill(c);
            }

            resolve(c);
          })
          .catch((error) => {
            bus.callHook("error", error);
          })
          .finally(() => {
            store.updating = false;
          });
      });
    };
  };

  const getShippingMethods = async () => {
    store.fetching = true;
    const result = await sales.checkout
      .getShippingMethods(store.checkout.token)
      .catch((error) => {
        bus.callHook("error", error);
      })
      .finally(() => {
        store.fetching = false;
      });

    if (result) {
      store.checkout.shippingMethods = result;
    }
  };

  if (token) {
    load(token);
  }

  const getWebUrl = () => {
    if (store.checkout.web_url) {
      const route = useRoute();
      return withQuery(store.checkout.web_url, omit(route.query, ["token"]));
    }
  };

  return {
    loading,
    store: readonly(store),
    data: readonly(store.checkout),
    load,
    create,
    update,
    getShippingMethods,
    onError,
    getWebUrl,
    setContact: wrapFnReturnCheckout(sales.checkout.setContact),
    setShippingAddress: wrapFnReturnCheckout(sales.checkout.setShippingAddress),
    setBillingAddress: wrapFnReturnCheckout(sales.checkout.setBillingAddress),
    useShippingAddressAsBilling: wrapFnReturnCheckout(
      sales.checkout.useShippingAddressAsBilling
    ),
    updateItemQuantity: wrapFnReturnCheckout(sales.checkout.updateItemQuantity),
    selectShipping: wrapFnReturnCheckout(sales.checkout.selectShipping),
    applyPromocode: wrapFnReturnCheckout(sales.checkout.applyPromocode),
    removePromocode: wrapFnReturnCheckout(sales.checkout.removePromocode),
    complete: wrapFnReturnCheckout(sales.checkout.complete),
  };
};
